/**
 * Shared FleetHub navbar + burger-menu styles.
 *
 * Moved verbatim out of `FleetHubScreen.web.tsx` (PLA-861) so the PSP view
 * (`/fleethub/overview`) renders the exact same header. Values unchanged.
 *
 * @module components/fleetHubHeader.styles
 */

import { TextStyle, ViewStyle } from "react-native"

import { colors, spacing } from "@/theme"

export const $header: ViewStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  paddingHorizontal: 24,
  paddingVertical: 8,
  backgroundColor: "rgba(255, 255, 255, 0.92)",
  // @ts-ignore
  backdropFilter: "blur(20px) saturate(1.2)",
  // @ts-ignore
  WebkitBackdropFilter: "blur(20px) saturate(1.2)",
  // @ts-ignore - Apple-style hairline border instead of spread shadow
  boxShadow: "0 1px 0 rgba(17, 30, 44, 0.06)",
  elevation: 4,
  zIndex: 1000,
}

export const $headerLeft: ViewStyle = {
  flexDirection: "column",
  gap: 4,
}

export const $headerSubtitle: TextStyle = {
  fontSize: 13,
  color: colors.textDim,
  fontWeight: "500",
  backgroundColor: colors.surfaceSecondary,
  paddingHorizontal: 10,
  paddingVertical: 4,
  borderRadius: 10,
  borderWidth: 1,
  borderColor: "rgba(17, 30, 44, 0.06)",
  alignSelf: "flex-start",
  marginTop: 2,
  maxWidth: "100%",
  overflow: "hidden",
}

export const $headerButtons: ViewStyle = {
  flexDirection: "row",
  gap: spacing.md, // 16px - increased from 12
  alignItems: "center",
}

export const HEADER_BUTTON_HEIGHT = 44

export const $headerButtonBase: ViewStyle = {
  minWidth: 140,
  height: HEADER_BUTTON_HEIGHT,
  paddingHorizontal: spacing.lg,
  backgroundColor: colors.palette.neutral800,
  borderWidth: 0,
  borderRadius: 10,
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  // @ts-ignore
  boxShadow: "0 1px 3px rgba(17, 30, 44, 0.12)",
  // @ts-ignore
  transition: "all 150ms ease",
  // @ts-ignore
  cursor: "pointer",
}

export const $headerButtonHover: ViewStyle = {
  backgroundColor: colors.surfaceDarkHover,
  // @ts-ignore
  transform: "scale(1.01)",
  // @ts-ignore
  boxShadow: "0 2px 6px rgba(17, 30, 44, 0.18)",
}

export const $headerButtonText: TextStyle = {
  color: colors.palette.neutral100, // White text
  fontSize: 15,
  fontWeight: "600",
  letterSpacing: 0, // Changed from 0.2 to fix React Native Web text node error
}

export const $iconAccessoryStyle: ViewStyle = {
  marginEnd: 0,
}

// Mobile-specific responsive styles
export const $headerMobile: ViewStyle = {
  flexDirection: "row", // Keep horizontal on mobile
  alignItems: "center", // Vertically center
  justifyContent: "space-between", // Space between logo and buttons
  paddingHorizontal: 8, // Reduced padding
  paddingVertical: 6,
  gap: 4, // Minimal gap
}

// Hamburger menu button — matches header button height
export const $hamburgerButton: ViewStyle = {
  width: HEADER_BUTTON_HEIGHT,
  height: HEADER_BUTTON_HEIGHT,
  borderRadius: 10,
  backgroundColor: colors.palette.neutral800,
  alignItems: "center",
  justifyContent: "center",
  // @ts-ignore
  boxShadow: "0 1px 3px rgba(17, 30, 44, 0.12)",
  // @ts-ignore
  transition: "all 150ms ease",
  // @ts-ignore
  cursor: "pointer",
}

// Mobile menu overlay
export const $mobileMenuBackdrop: ViewStyle = {
  // @ts-ignore - Web fixed positioning
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1001,
}

// Mobile menu dropdown
export const $mobileMenuDropdown: ViewStyle = {
  // @ts-ignore - Web fixed positioning
  position: "fixed",
  top: 72,
  right: 8,
  width: 200,
  backgroundColor: "rgba(255, 255, 255, 0.95)",
  borderRadius: 12,
  paddingVertical: 4,
  zIndex: 1002,
  // @ts-ignore
  backdropFilter: "blur(20px) saturate(1.2)",
  // @ts-ignore
  WebkitBackdropFilter: "blur(20px) saturate(1.2)",
  // @ts-ignore
  boxShadow: "0 4px 16px rgba(17, 30, 44, 0.15)",
  // @ts-ignore
  animation: "mobileMenuIn 150ms ease-out",
}

export const $mobileMenuItem: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: 12,
  paddingHorizontal: 16,
  paddingVertical: 12,
  // @ts-ignore
  cursor: "pointer",
  // @ts-ignore
  transition: "background-color 100ms ease",
}

export const $mobileMenuItemHover: ViewStyle = {
  backgroundColor: "rgba(17, 30, 44, 0.05)",
}

export const $mobileMenuItemText: TextStyle = {
  fontSize: 15,
  fontWeight: "500",
  color: colors.palette.neutral800,
}

export const $mobileMenuDivider: ViewStyle = {
  height: 1,
  backgroundColor: "rgba(17, 30, 44, 0.06)",
  marginHorizontal: 12,
}
